import type React from 'react'

import { ButtonGroup, useBreakpoint } from 'slab'

import { type Step } from '../types'
import { QLEActionMenu } from './QLEActionMenu/QLEActionMenu'

export interface QLEButtonLayoutProps {
  backButton: React.ReactNode | null;
  rightButtons: React.ReactNode | React.ReactNode[];
  /** When provided, renders this instead of QLEActionMenu (e.g. single "Cancel change" button for QLE start/details page) */
  actionMenuContent?: React.ReactNode;
  navigateToSpecificStep?: (step: Step) => void;
}

/**
 * Layout component for QLE flow pages that ensures consistent button placement:
 * - Left: QLEActionMenu (or actionMenuContent) + Back button
 * - Right: Main action buttons (grouped if multiple)
 * - Mobile: Same order, stacked vertically
 */
export function QLEButtonLayout ({
  backButton,
  rightButtons,
  actionMenuContent,
  navigateToSpecificStep,
}: QLEButtonLayoutProps) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const isMobile = !breakpointIsAtLeast('xs')

  // Ensure rightButtons is an array
  const rightButtonsArray = Array.isArray(rightButtons)
    ? rightButtons
    : [rightButtons].filter(Boolean)

  const leftContent = actionMenuContent ?? (
    <QLEActionMenu navigateToSpecificStep={navigateToSpecificStep} />
  )

  // Mobile layout: main buttons on the top, back button and left content on the bottom
  if (isMobile) {
    return (
      <div className='flex flex-col gap-2'>
        <ButtonGroup size='large' layout='vertical'>
          {rightButtonsArray}
        </ButtonGroup>
        <ButtonGroup size='large' layout='vertical'>
          {backButton}
          {leftContent}
        </ButtonGroup>
      </div>
    )
  }

  // Desktop layout: left content and Back on left, action buttons on right
  return (
    <div className='flex items-center justify-between'>
      <ButtonGroup size='large' align='left'>
        {leftContent}
        {backButton}
      </ButtonGroup>
      <ButtonGroup size='large' align='right'>
        {rightButtonsArray}
      </ButtonGroup>
    </div>
  )
}
